import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AdUnit from '../components/AdUnit';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

// Get site domain from environment variables
const SITE_DOMAIN = process.env.REACT_APP_SITE_DOMAIN || 'https://yourjsontools.com';

const DummyImagesPage = () => {
  // State for custom image size inputs
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');

  // Image size options
  const imageSizes = [
    { width: 100, height: 100, label: '100x100' },
    { width: 200, height: 200, label: '200x200' },
    { width: 300, height: 300, label: '300x300' },
    { width: 400, height: 400, label: '400x400' },
    { width: 500, height: 500, label: '500x500' },
    { width: 800, height: 600, label: '800x600' },
    { width: 1024, height: 768, label: '1024x768' },
    { width: 1920, height: 1080, label: '1920x1080' }
  ];

  // Image formats
  const imageFormats = [
    { format: 'png', label: 'PNG', mimeType: 'image/png', quality: 1 },
    { format: 'jpeg', label: 'JPEG', mimeType: 'image/jpeg', quality: 0.9 },
    { format: 'webp', label: 'WebP', mimeType: 'image/webp', quality: 0.9 }
  ];

  // Generate dummy image
  const generateImage = (width, height, format, mimeType, quality) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#3498db');
    gradient.addColorStop(1, '#2c3e50');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Add text
    ctx.fillStyle = 'white';
    ctx.font = `${Math.min(width, height) / 10}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${width}x${height}`, width / 2, height / 2);

    // Add format label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.font = `${Math.min(width, height) / 20}px Arial`;
    ctx.fillText(format.toUpperCase(), width / 2, height / 2 + Math.min(width, height) / 8);

    // Add border
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);

    // Convert to blob and download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dummy-image-${width}x${height}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, mimeType, quality);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Dummy Images Generator",
    "url": `${SITE_DOMAIN}/dummy-files/images`,
    "description": "Generate dummy placeholder images in various sizes and formats (PNG, JPEG, WebP) for testing and development purposes.",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <>
      <SEO 
        title="Dummy Images Generator - Create Test Images | yourjsontools.com"
        description="Generate dummy placeholder images in various sizes and formats (PNG, JPEG, WebP) for testing and development. Download instantly in multiple formats."
        keywords={["dummy images", "test images", "placeholder images", "image generator", "PNG", "JPEG", "WebP", "testing tools", "development tools"]}
        canonicalUrl={`${SITE_DOMAIN}/dummy-files/images`}
        structuredData={jsonLd}
      />

      <div className="page-container">
        <div className="page-header">
          <nav className="breadcrumb">
            <Link to="/dummy-files">Dummy Files</Link> / <span>Images</span>
          </nav>
          <h1>Dummy Images Generator</h1>
          <p className="page-description">
            Generate placeholder images in various sizes and formats (PNG, JPEG, WebP) for web development, design mockups, and testing purposes.
          </p>
        </div>

        <div className="predefined-sizes-section">
          <h2>Predefined Sizes</h2>
          <p className="section-description">
            Quick access to commonly used image dimensions for web development and design.
          </p>
        </div>

        <div className="tool-section">
          <div className="images-grid">
            {imageSizes.map((size, index) => (
              <div key={index} className="image-card">
                <div className="image-preview">
                  <div 
                    className="preview-box"
                    style={{
                      width: '120px',
                      height: `${(120 * size.height) / size.width}px`,
                      background: 'linear-gradient(135deg, #3498db, #2c3e50)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      borderRadius: '4px'
                    }}
                  >
                    {size.label}
                  </div>
                </div>
                <div className="image-info">
                  <h3>{size.label}</h3>
                  <p>{size.width} × {size.height} pixels</p>
                  <div className="format-buttons">
                    {imageFormats.map((formatInfo, formatIndex) => (
                      <button
                        key={formatIndex}
                        className={`format-button format-${formatInfo.format}`}
                        onClick={() => generateImage(size.width, size.height, formatInfo.format, formatInfo.mimeType, formatInfo.quality)}
                      >
                        {formatInfo.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="divider"></div>

        {/* Custom Size Section */}
        <div className="custom-size-section">
          <h2>Generate Custom Size Image</h2>
          <p className="section-description">
            Enter custom width and height dimensions to generate images in any size you need.
          </p>
          
          <div className="custom-size-form">
            <div className="input-group">
              <label htmlFor="width-input">Width (pixels)</label>
              <input
                id="width-input"
                type="number"
                min="1"
                max="5000"
                value={customWidth}
                onChange={(e) => setCustomWidth(e.target.value)}
                placeholder="e.g. 800"
                className="size-input"
              />
            </div>
            
            <div className="input-group">
              <label htmlFor="height-input">Height (pixels)</label>
              <input
                id="height-input"
                type="number"
                min="1"
                max="5000"
                value={customHeight}
                onChange={(e) => setCustomHeight(e.target.value)}
                placeholder="e.g. 600"
                className="size-input"
              />
            </div>
          </div>

          {customWidth && customHeight && parseInt(customWidth) > 0 && parseInt(customHeight) > 0 && (
            <div className="custom-preview">
              <div className="preview-wrapper">
                <div 
                  className="custom-preview-box"
                  style={{
                    width: Math.min(200, parseInt(customWidth)),
                    height: Math.min(200, (parseInt(customHeight) * Math.min(200, parseInt(customWidth))) / parseInt(customWidth)),
                    background: 'linear-gradient(135deg, #3498db, #2c3e50)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    borderRadius: '4px',
                    border: '2px solid #ecf0f1'
                  }}
                >
                  {customWidth}×{customHeight}
                </div>
              </div>
              
              <div className="custom-format-buttons">
                {imageFormats.map((formatInfo, formatIndex) => (
                  <button
                    key={formatIndex}
                    className={`format-button format-${formatInfo.format}`}
                    onClick={() => generateImage(parseInt(customWidth), parseInt(customHeight), formatInfo.format, formatInfo.mimeType, formatInfo.quality)}
                    disabled={!customWidth || !customHeight || parseInt(customWidth) < 1 || parseInt(customHeight) < 1}
                  >
                    Generate {formatInfo.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="info-section">
          <h2>About Dummy Images</h2>
          <div className="info-grid">
            <div className="info-card">
              <h3>🎨 Multiple Formats</h3>
              <p>
                Generate images in PNG, JPEG, and WebP formats. Each format is optimized 
                for different use cases and compression needs.
              </p>
            </div>
            <div className="info-card">
              <h3>📐 Standard Sizes</h3>
              <p>
                Common web and design sizes from small thumbnails to full HD resolution. 
                Covers most use cases for development and testing.
              </p>
            </div>
            <div className="info-card">
              <h3>⚡ Instant Download</h3>
              <p>
                Images are generated instantly in your browser and downloaded immediately. 
                No server processing or waiting time.
              </p>
            </div>
            <div className="info-card">
              <h3>🔒 Privacy First</h3>
              <p>
                All generation happens locally in your browser. No data is sent to servers, 
                ensuring complete privacy.
              </p>
            </div>
          </div>
        </div>

        <div className="formats-section">
          <h2>Image Formats</h2>
          <div className="formats-grid">
            <div className="format-info-card">
              <h3>📷 PNG</h3>
              <p>
                <strong>Lossless compression</strong> - Perfect for images with transparency, 
                logos, and graphics that need crisp edges. Larger file sizes but highest quality.
              </p>
            </div>
            <div className="format-info-card">
              <h3>🖼️ JPEG</h3>
              <p>
                <strong>Lossy compression</strong> - Ideal for photographs and images with many colors. 
                Smaller file sizes with good quality, widely supported across all platforms.
              </p>
            </div>
            <div className="format-info-card">
              <h3>🚀 WebP</h3>
              <p>
                <strong>Modern format</strong> - Superior compression with excellent quality. 
                Smaller file sizes than JPEG/PNG, perfect for web optimization and modern browsers.
              </p>
            </div>
          </div>
        </div>

      </div>

      <Footer />

      <style jsx>{`
        .breadcrumb {
          margin-bottom: 1rem;
          font-size: 0.9rem;
          color: #666;
        }

        .breadcrumb a {
          color: #3498db;
          text-decoration: none;
        }

        .breadcrumb a:hover {
          text-decoration: underline;
        }

        .custom-size-section {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .custom-size-section h2 {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
          font-size: 1.5rem;
        }

        .section-description {
          margin: 0 0 1.5rem 0;
          color: #7f8c8d;
          line-height: 1.6;
        }

        .custom-size-form {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          align-items: end;
        }

        .input-group {
          flex: 1;
        }

        .input-group label {
          display: block;
          margin-bottom: 0.5rem;
          color: #2c3e50;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .size-input {
          width: 100%;
          padding: 0.8rem;
          border: 2px solid #ecf0f1;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        .size-input:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
        }

        .size-input::placeholder {
          color: #bdc3c7;
        }

        .custom-preview {
          display: flex;
          gap: 2rem;
          align-items: center;
          padding: 1.5rem;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #ecf0f1;
        }

        .preview-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          min-width: 200px;
        }

        .custom-format-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .custom-format-buttons .format-button {
          min-width: 100px;
          padding: 0.8rem 1.2rem;
        }

        .custom-format-buttons .format-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .divider {
          height: 1px;
          background: linear-gradient(to right, transparent, #ecf0f1, transparent);
          margin: 3rem 0;
        }

        .predefined-sizes-section h2 {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
          font-size: 1.5rem;
        }

        .images-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .image-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          text-align: center;
        }

        .image-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .image-preview {
          margin-bottom: 1rem;
          display: flex;
          justify-content: center;
        }

        .preview-box {
          border: 2px solid #ecf0f1;
        }

        .image-info h3 {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
          font-size: 1.2rem;
        }

        .image-info p {
          margin: 0 0 1rem 0;
          color: #7f8c8d;
          font-size: 0.9rem;
        }

        .format-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
        }

        .format-button {
          border: none;
          padding: 0.6rem 1rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          transition: all 0.2s ease;
          flex: 1;
          min-width: 60px;
        }

        .format-png {
          background: #3498db;
          color: white;
        }

        .format-png:hover {
          background: #2980b9;
        }

        .format-jpeg {
          background: #e67e22;
          color: white;
        }

        .format-jpeg:hover {
          background: #d35400;
        }

        .format-webp {
          background: #27ae60;
          color: white;
        }

        .format-webp:hover {
          background: #229954;
        }

        .info-section, .formats-section {
          margin-top: 3rem;
        }

        .info-grid, .formats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-top: 1.5rem;
        }

        .info-card, .format-info-card {
          background: white;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .info-card h3, .format-info-card h3 {
          margin: 0 0 1rem 0;
          color: #2c3e50;
        }

        .info-card p, .format-info-card p {
          margin: 0;
          color: #7f8c8d;
          line-height: 1.6;
        }

        .format-info-card strong {
          color: #2c3e50;
        }

        @media (max-width: 768px) {
          .images-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 1rem;
          }

          .image-card {
            padding: 1rem;
          }

          .format-buttons {
            flex-direction: column;
            gap: 0.5rem;
          }

          .format-button {
            min-width: auto;
          }

          .custom-size-section {
            padding: 1.5rem;
            margin-bottom: 1.5rem;
          }

          .custom-size-form {
            flex-direction: column;
            gap: 1rem;
          }

          .custom-preview {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }

          .custom-format-buttons {
            justify-content: center;
            width: 100%;
          }

          .custom-format-buttons .format-button {
            flex: 1;
            min-width: auto;
          }
        }
      `}</style>
    </>
  );
};

export default DummyImagesPage; 